/**
 * S1 · Social Posts screen — mounted at /social. Distinct from the older
 * /feed screen (which reads legacy `db.feed_posts`); this uses the new
 * `db.social_posts` collection with visibility + moderation + blocks.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { MessageCircle, Flag, Trash2, Send, ChevronLeft, Globe2, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function SocialPosts() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("public");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/social/feed", { params: { scope } });
      setItems(data.items || []);
    } catch { toast.error(t("social.feed.error", "Could not load feed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const post = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/social/posts", { text, visibility: "public" });
      setItems([data, ...items]); setText("");
      toast.success(t("social.posted", "Posted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Something went wrong"));
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="social-posts">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label={t("common.back", "Back")}><ChevronLeft size={20}/></button>
          <div className="font-black text-stone-900 text-base flex-1">{t("social.title", "Social")}</div>
          <div className="flex gap-1 text-xs">
            {["public", "followers", "mine"].map(s => (
              <button key={s} onClick={() => setScope(s)}
                      data-testid={`social-tab-${s}`}
                      className={`px-3 py-1 rounded-full font-black border ${scope === s ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}>
                {t(`social.scope.${s}`, s)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-4 space-y-3">
        <div className="rounded-2xl bg-white border border-stone-200 p-3" data-testid="social-composer">
          <textarea value={text} onChange={e => setText(e.target.value)}
                    placeholder={t("social.composer.placeholder", "Share what's happening nearby…")}
                    rows={3} maxLength={4000}
                    data-testid="social-composer-text"
                    className="w-full text-sm outline-none resize-none"/>
          <div className="flex items-center gap-2 justify-between mt-1">
            <div className="text-[10px] text-stone-500 inline-flex items-center gap-1"><Globe2 size={11}/> {t("social.visibility.public", "Public")}</div>
            <button onClick={post} disabled={!text.trim()}
                    data-testid="social-composer-post"
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
              <Send size={12}/> {t("social.post", "Post")}
            </button>
          </div>
        </div>

        {busy && !items.length && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !items.length && <div className="text-center py-16 text-stone-400" data-testid="social-empty">{t("social.empty", "Nothing here yet — be the first to post.")}</div>}
        {items.map(p => <PostCard key={p.post_id} post={p} onDelete={() => setItems(items.filter(x => x.post_id !== p.post_id))}/>)}
      </div>
    </div>
  );
}

function PostCard({ post, onDelete }) {
  const { t } = useI18n();
  const [openComments, setOpenComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [c, setC] = useState("");
  const loadC = async () => {
    try { const { data } = await api.get(`/social/post/${post.post_id}/comments`); setComments(data.items || []); }
    catch { /* silent */ }
  };
  const addC = async () => {
    if (!c.trim()) return;
    try { const { data } = await api.post(`/social/post/${post.post_id}/comments`, { text: c }); setComments([data, ...comments]); setC(""); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.error", "Something went wrong")); }
  };
  const report = async () => {
    const reason = window.prompt(t("social.report.reason", "Reason for report?"));
    if (!reason) return;
    try { await api.post(`/social/post/${post.post_id}/report`, { reason }); toast.success(t("social.reported", "Reported. Thanks.")); }
    catch { toast.error(t("common.error", "Something went wrong")); }
  };
  const del = async () => {
    if (!window.confirm(t("social.delete.confirm", "Delete this post?"))) return;
    try { await api.delete(`/social/post/${post.post_id}`); onDelete?.(); }
    catch { toast.error(t("common.error", "Something went wrong")); }
  };
  useEffect(() => { if (openComments) loadC(); /* eslint-disable-next-line */ }, [openComments]);
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-4" data-testid={`social-post-${post.post_id}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-stone-100 grid place-items-center"><UserRound size={14}/></div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-stone-900 text-sm truncate">{post.author_name}</div>
          <div className="text-[10px] text-stone-500">{post.city || "—"} · {(post.created_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <button onClick={report} aria-label={t("social.report", "Report")} data-testid={`social-report-${post.post_id}`} className="text-stone-400 hover:text-red-600"><Flag size={14}/></button>
        <button onClick={del}    aria-label={t("common.delete", "Delete")} data-testid={`social-delete-${post.post_id}`} className="text-stone-300 hover:text-red-600"><Trash2 size={14}/></button>
      </div>
      <div className="text-sm text-stone-800 mt-2 whitespace-pre-wrap break-words">{post.text}</div>
      <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
        <button onClick={() => setOpenComments(!openComments)}
                data-testid={`social-comments-toggle-${post.post_id}`}
                className="inline-flex items-center gap-1 font-black hover:text-stone-900"><MessageCircle size={12}/> {post.comment_count || 0}</button>
        <Link to={`/connect?to=${post.author_id}`} className="ml-auto text-emerald-700 font-black hover:underline" data-testid={`social-message-${post.post_id}`}>{t("social.message", "Message")}</Link>
      </div>
      {openComments && (
        <div className="mt-2 pt-2 border-t border-stone-100 space-y-2" data-testid={`social-comments-${post.post_id}`}>
          <div className="flex gap-2">
            <input value={c} onChange={e => setC(e.target.value)}
                   placeholder={t("social.comment.placeholder", "Add a comment…")}
                   data-testid={`social-comment-input-${post.post_id}`}
                   className="flex-1 text-sm border border-stone-200 rounded-full px-3 py-1.5"/>
            <button onClick={addC} data-testid={`social-comment-submit-${post.post_id}`}
                    className="rounded-full bg-emerald-600 text-white text-xs font-black px-3 py-1.5">{t("common.send", "Send")}</button>
          </div>
          {comments.map(cm => (
            <div key={cm.comment_id} className="text-xs" data-testid={`social-comment-${cm.comment_id}`}>
              <span className="font-black text-stone-900">{cm.author_name}: </span>
              <span className="text-stone-700">{cm.text}</span>
            </div>
          ))}
          {!comments.length && <div className="text-[11px] text-stone-400">{t("social.no_comments", "No comments yet.")}</div>}
        </div>
      )}
    </div>
  );
}
